// Theme API helpers for GYM-596 tenant white-label.
// Public endpoint is unauthenticated; owner endpoints use apiFetch.

import { apiFetch } from "@/lib/api/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PublicTheme {
  legal_name: string;
  brand_primary_color: string | null;
  brand_accent_color: string | null;
  brand_font_family: string | null;
  logo_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
  login_ads: string[] | null;
}

export interface TenantTheme {
  brand_primary_color: string | null;
  brand_accent_color: string | null;
  brand_font_family: string | null;
  logo_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
  theme_is_published: boolean;
  login_ads: string[];
  contrast_primary_on_white: number | null;
  contrast_passes_aa: boolean | null;
  auto_tone_suggestion: string | null;
}

export type TenantThemePatch = Partial<
  Pick<
    TenantTheme,
    | "brand_primary_color"
    | "brand_accent_color"
    | "brand_font_family"
    | "logo_url"
    | "logo_dark_url"
    | "favicon_url"
    | "theme_is_published"
  >
>;

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** Unauthenticated — safe to call before member login. */
export async function getPublicTheme(): Promise<PublicTheme> {
  const res = await fetch("/api/v1/tenants/public/theme/");
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<PublicTheme>;
}

/** Authenticated — owner only. */
export function getTenantTheme(): Promise<TenantTheme> {
  return apiFetch<TenantTheme>("/tenants/theme/");
}

/** Authenticated PATCH/PUT — owner only. */
export function putTenantTheme(data: TenantThemePatch): Promise<TenantTheme> {
  return apiFetch<TenantTheme>("/tenants/theme/", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// Tenant identity (legal name, TRN, etc.)
// ---------------------------------------------------------------------------

export type PosPaymentMethodKey = "cash" | "manual_card" | "bank_transfer" | "tabby" | "tamara" | "payment_link";

export interface TenantIdentity {
  legal_name: string;
  trading_name: string;
  trn: string;
  locale_default: "en" | "ar";
  vat_registered: boolean;
  whatsapp_manual_send_enabled: boolean;
  currency: string;
  timezone: string;
  pos_payment_methods: PosPaymentMethodKey[];
}

/** Authenticated — owner only. */
export function getTenantIdentity(): Promise<TenantIdentity> {
  return apiFetch<TenantIdentity>("/tenants/identity/");
}

/** Authenticated PATCH — owner only. */
export function patchTenantIdentity(data: Partial<TenantIdentity>): Promise<TenantIdentity> {
  return apiFetch<TenantIdentity>("/tenants/identity/", {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// Logo upload
// ---------------------------------------------------------------------------

/** Authenticated — upload a logo file, returns the stored media URL. */
export function uploadTenantLogo(file: File): Promise<{ logo_url: string }> {
  const form = new FormData();
  form.append("file", file);
  return apiFetch<{ logo_url: string }>("/tenants/logo-upload/", {
    method: "POST",
    body: form,
  });
}

// ---------------------------------------------------------------------------
// Login ad images
// ---------------------------------------------------------------------------

/** Authenticated — upload a login-panel ad image, returns updated ads list. */
export function uploadLoginAd(file: File): Promise<{ login_ads: string[] }> {
  const form = new FormData();
  form.append("file", file);
  return apiFetch<{ login_ads: string[] }>("/tenants/login-ad-upload/", {
    method: "POST",
    body: form,
  });
}

/** Authenticated — remove a login-panel ad by URL, returns updated ads list. */
export function deleteLoginAd(url: string): Promise<{ login_ads: string[] }> {
  return apiFetch<{ login_ads: string[] }>("/tenants/login-ad-upload/", {
    method: "DELETE",
    body: JSON.stringify({ url }),
  });
}
